/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Added line variant with CVA; Context-based variant propagation; sliding
 * indicator behind the active trigger on the default variant
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      default: 'relative isolate bg-muted/60 border border-border-light text-muted-foreground h-9 w-fit rounded-full p-0 justify-center',
      line: 'relative items-end bg-background border-b rounded-none w-full gap-6 justify-start p-0 pb-[6px] justify-center',
      outline: 'text-muted-foreground text-xs justify-start flex-wrap',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabsTriggerVariants = cva(
  'relative inline-flex items-center justify-center gap-1.5 cursor-pointer text-sm font-medium whitespace-nowrap transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  {
    variants: {
      variant: {
        default: `relative z-10 outline-none focus-visible:highlight-border-glow text-muted-foreground hover:text-foreground
                  data-[state=active]:text-foreground data-[state=active]:font-semibold h-full flex-1 rounded-full px-5 py-1 transition-colors`,
        line: `text-base text-muted-foreground max-w-max px-0 px-2 py-0.75 rounded-md hover:bg-muted transition-all
                  data-[state=active]:text-accent-foreground data-[state=active]:bg-muted/50 data-[state=active]:hover:bg-accent
                  focus-visible:highlight-border-glow border border-transparent`,
        outline: `max-w-max outline-none px-2 flex-1 min-h-7 shadow-xs
                  text-foreground border bg-card [&_svg]:opacity-40 rounded-[min(var(--radius-md),10px)] transition-all hover:bg-soft focus-visible:highlight-border-glow
                  data-[state=active]:bg-accent/60 data-[state=active]:border-ring/25 data-[state=active]:text-accent-foreground data-[state=active]:hover:bg-accent/80 data-[state=active]:[&>svg]:text-accent-foreground data-[state=active]:[&_svg]:opacity-50`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type TabsVariant = VariantProps<typeof tabsListVariants>['variant'];

const TabsContext = React.createContext<{ variant?: TabsVariant }>({});

function Tabs({ className, variant = 'default', ...props }: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
  return (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root data-slot="tabs" className={cn('flex flex-col', className)} {...props} />
    </TabsContext.Provider>
  );
}

function TabsList({
  className,
  variant: variantProp,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
  const context = React.useContext(TabsContext);
  const variant = variantProp ?? context.variant;
  const listRef = React.useRef<HTMLDivElement>(null);
  const { box: indicator, settled } = useActiveTriggerBox(listRef, variant === 'default' || variant === 'line');
  // No transition on the first paint: the indicator would otherwise slide in from the list's left edge.
  const slide = settled ? 'transition-[transform,width] duration-200 ease-out' : '';

  return (
    <TabsPrimitive.List ref={listRef} data-slot="tabs-list" className={cn(tabsListVariants({ variant }), className)} {...props}>
      {variant === 'default' && (
        <span
          aria-hidden="true"
          data-slot="tabs-indicator"
          className={cn(
            'pointer-events-none absolute -inset-y-0.5 left-0 z-0 rounded-full bg-card border border-border-heavy shadow-md/3',
            slide,
            !indicator && 'opacity-0'
          )}
          style={indicator ? { width: indicator.width + 4, transform: `translateX(${indicator.left - 2}px)` } : undefined}
        />
      )}
      {variant === 'line' && (
        <span
          aria-hidden="true"
          data-slot="tabs-indicator"
          className={cn('pointer-events-none absolute -bottom-px left-0 h-0.5 rounded-full bg-ring', slide, !indicator && 'opacity-0')}
          style={indicator ? { width: indicator.width, transform: `translateX(${indicator.left}px)` } : undefined}
        />
      )}
      {props.children}
    </TabsPrimitive.List>
  );
}

/**
 * Where the active trigger sits inside the list, so a single indicator can slide between
 * triggers instead of each trigger painting its own background. Radix flips `data-state` on
 * the triggers without telling the list, hence the attribute observer.
 */
function useActiveTriggerBox(listRef: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
  const [box, setBox] = React.useState<{ left: number; width: number } | null>(null);
  const [settled, setSettled] = React.useState(false);

  // Arm the transition once the first measurement has been painted in place. The timer backs
  // up the frame callback, which browsers pause while the tab is hidden.
  React.useEffect(() => {
    if (!box || settled) return;
    const arm = () => setSettled(true);
    const frame = requestAnimationFrame(arm);
    const timer = setTimeout(arm, 100);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [box, settled]);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!enabled || !list) return;
    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]');
      if (!active) return setBox(null);
      // Measured against the list's padding box rather than via offsetLeft, so triggers nested
      // in their own positioned wrapper still land the indicator under the right tab.
      const listRect = list.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      setBox({ left: rect.left - listRect.left - list.clientLeft + list.scrollLeft, width: rect.width });
    };
    measure();
    const attrs = new MutationObserver(measure);
    attrs.observe(list, { attributes: true, attributeFilter: ['data-state'], subtree: true, childList: true });
    const size = new ResizeObserver(measure);
    size.observe(list);
    return () => {
      attrs.disconnect();
      size.disconnect();
    };
  }, [listRef, enabled]);

  return { box, settled };
}

function TabsTrigger({
  className,
  variant: variantProp,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & VariantProps<typeof tabsTriggerVariants>) {
  const context = React.useContext(TabsContext);
  const variant = variantProp ?? context.variant;

  return <TabsPrimitive.Trigger data-slot="tabs-trigger" className={cn(tabsTriggerVariants({ variant }), className)} {...props} />;
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn('flex-1 outline-none', className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
